import { useId, type ComponentProps } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '../../lib/utils.ts';
import { fieldClass } from './variants.ts';

/** Gesperrt: `true` oder ein Grund, den Hilfsmittel nach „nicht änderbar“ vorlesen (z. B. „kommt aus Shopware“). */
export type FieldLock = true | string;

export type InputProps = ComponentProps<'input'> & {
  invalid?: boolean;
  unit?: string | undefined;
  /** Gesperrt mit Schloss rechts im Feld; setzt `readOnly`. */
  locked?: FieldLock | undefined;
};

/**
 * Platz rechts im Feld für Einheit und Schloss, damit der Wert (auch rechtsbündig) nie darunter läuft. Grob nach Länge
 * der Einheit: „%“, „€“ schmal; „Tage“, „Stück“ mittel; „Minuten“ etwas breiter; „Sekunden“, „Arbeitstage“ breit.
 */
export function endPadding(unit: string | undefined, locked: boolean): string | undefined {
  const len = unit?.length ?? 0;
  const unitPad = len === 0 ? 0 : len <= 2 ? 1 : len <= 5 ? 2 : len <= 7 ? 3 : len <= 8 ? 4 : 5;
  const step = Math.min(5, unitPad + (locked ? 1 : 0));
  return [undefined, 'pr-[var(--field-end-icon)]', 'pr-[var(--field-end-2)]', 'pr-[var(--field-end-3)]', 'pr-[var(--field-end-4)]', 'pr-[var(--field-end-5)]'][locked && unitPad === 0 ? 1 : step];
}

/** Mehrere ids für `aria-describedby`, leere weglassen. */
export function joinIds(...ids: (string | undefined)[]): string | undefined {
  return ids.filter(Boolean).join(' ') || undefined;
}

/** Text für Hilfsmittel zu einem gesperrten Feld. */
export function lockText(locked: FieldLock): string {
  return locked === true ? 'nicht änderbar' : `nicht änderbar: ${locked}`;
}

/**
 * Formularfeld (Baustein A, Ticket 1.256): weiß mit klarem grauem Rand (Rudolf 21.10.2026), Fokus 2 px blauer Rand, Fehler 2 px rot
 * (`fieldClass`). `unit` (z. B. „Tage“, „kg“): Einheit grau rechts im Feld, nie im Label (DESIGN Do's and Don'ts
 * „Einheit im Feld“). `locked`: gesperrt ohne Fläche, Schloss rechts, für Hilfsmittel „nicht änderbar“ (+ Grund);
 * `readOnly` allein zeigt das gesperrte Aussehen ohne Schloss.
 */
export function Input({ className, invalid, unit, locked, readOnly, 'aria-describedby': describedBy, ...props }: InputProps) {
  const lockId = useId();
  const isLocked = !!locked || !!readOnly;
  const input = (
    <input
      aria-invalid={invalid || undefined}
      aria-describedby={joinIds(describedBy, locked ? lockId : undefined)}
      readOnly={isLocked || undefined}
      data-field-box={isLocked ? 'locked' : 'edit'}
      className={cn(fieldClass({ invalid, locked: isLocked }), 'h-11 w-full px-space-4', endPadding(unit, !!locked), className)}
      {...props}
    />
  );
  if (!unit && !locked) return input;
  return (
    <div className="relative">
      {input}
      <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-space-2 text-ink-2">
        {unit ? (
          <span className="type-meta">
            {unit}
          </span>
        ) : null}
        {locked ? (
          <>
            <Lock size={16} strokeWidth={1.5} aria-hidden data-testid="feld-schloss" />
            <span id={lockId} className="sr-only">
              {lockText(locked)}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}
